import React, { useState } from 'react';
import { GolfCourse } from '../../services/api';
import { MapPin, Search, X } from 'lucide-react';

interface CourseSelectorProps {
  golfCourses: GolfCourse[];
  selectedCourseIds: number[];
  onCourseChange: (courseId: number, checked: boolean) => void;
  isLoading: boolean;
  error?: string;
}

const CourseSelector: React.FC<CourseSelectorProps> = ({
  golfCourses,
  selectedCourseIds,
  onCourseChange,
  isLoading,
  error
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('all');

  // Dynamically extract unique locations from golf courses
  const availableLocations = React.useMemo(() => {
    const locations = Array.from(new Set(golfCourses.map(course => course.location).filter(Boolean))).sort();
    return locations;
  }, [golfCourses]);

  // Get count of selected courses per location
  const getSelectedCountByLocation = (location: string): number => {
    return golfCourses
      .filter(course => course.location === location && selectedCourseIds.includes(course.id))
      .length;
  };

  // Get total count of courses per location
  const getTotalCountByLocation = (location: string): number => {
    return golfCourses.filter(course => course.location === location).length;
  };


  // Generate location display info dynamically
  const getLocationDisplayName = (location: string): string => {
    return location.replace(', BC', '');
  };

  // Filter golf courses
  const filteredCourses = golfCourses.filter(course => {
    const matchesSearch = course.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesLocation = selectedLocation === 'all' || course.location === selectedLocation;
    return matchesSearch && matchesLocation;
  });

  return (
    <div className="bg-gradient-to-r from-green-50 to-blue-50 p-4 md:p-6 rounded-lg border border-green-200">
      {/* Header Row with Title and Selection Status */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0 mb-3 md:mb-4">
        <label className="flex items-center text-base md:text-lg font-medium text-gray-800">
          <MapPin className="text-green-600 mr-2" size={18} />
          Select Golf Courses *
        </label>
        
        {/* Selection Status and Clear Button - Fixed Height */}
        <div className="flex items-center gap-2 sm:justify-end min-h-[24px]">
          {selectedCourseIds.length > 0 ? (
            <>
              <span className="text-sm text-blue-700">
                {selectedCourseIds.length} selected
              </span>
              <button
                type="button"
                onClick={() => selectedCourseIds.forEach(id => onCourseChange(id, false))}
                className="px-2 py-1 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded transition-colors"
              >
                Clear
              </button>
            </>
          ) : (
            <span className="text-sm text-gray-500">Start selecting</span>
          )}
        </div>
      </div>
      
      {/* Search Box */}
      <div className="relative mb-4">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="h-5 w-5 text-gray-400" />
        </div>
        <input
          type="text"
          placeholder="Search golf courses..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
        />
        {searchTerm && (
          <button
            onClick={() => setSearchTerm('')}
            className="absolute inset-y-0 right-0 pr-3 flex items-center"
          >
            <X className="h-5 w-5 text-gray-400 hover:text-gray-600" />
          </button>
        )}
      </div>

      {/* Mobile: Horizontal tabs, Desktop: Sidebar layout */}
      <div className="md:flex md:gap-6 md:h-96">
        {/* Mobile: Horizontal Location Filter */}
        <div className="md:hidden mb-4">
          <div className="flex gap-2 overflow-x-auto pb-2">
            <button
              type="button"
              onClick={() => setSelectedLocation('all')}
              className={`flex-shrink-0 px-3 py-2 text-sm rounded transition-colors ${
                selectedLocation === 'all'
                  ? 'bg-blue-500 text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              All ({golfCourses.length})
            </button>
            
            {availableLocations.map((location) => {
              if (!location) return null;
              const selectedCount = getSelectedCountByLocation(location);
              const totalCount = getTotalCountByLocation(location);
              return (
                <button
                  key={location}
                  type="button"
                  onClick={() => setSelectedLocation(location)}
                  className={`flex-shrink-0 px-3 py-2 text-sm rounded transition-colors whitespace-nowrap ${
                    selectedLocation === location
                      ? 'bg-blue-500 text-white'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {getLocationDisplayName(location)} ({selectedCount > 0 ? `${selectedCount}/${totalCount}` : totalCount})
                </button>
              );
            })}
          </div>
        </div>

        {/* Desktop: Sidebar - Location Filter */}
        <div className="hidden md:block w-48 flex-shrink-0 overflow-y-auto border border-gray-200 rounded-md bg-white">
          <div className="p-2 space-y-1">
            <button
              type="button"
              onClick={() => setSelectedLocation('all')}
              className={`w-full text-left px-3 py-2 text-sm rounded transition-colors ${
                selectedLocation === 'all'
                  ? 'bg-blue-500 text-white'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <div className="flex justify-between items-center">
                <span>All Courses</span>
                <span className={`text-xs ${
                  selectedLocation === 'all' ? 'text-blue-100' : 'text-gray-500'
                }`}>
                  {golfCourses.length}
                </span>
              </div>
            </button>
            
            {availableLocations.map((location) => {
              if (!location) return null;
              const selectedCount = getSelectedCountByLocation(location);
              const totalCount = getTotalCountByLocation(location);
              return (
                <button
                  key={location}
                  type="button"
                  onClick={() => setSelectedLocation(location)}
                  className={`w-full text-left px-3 py-2 text-sm rounded transition-colors ${
                    selectedLocation === location
                      ? 'bg-blue-500 text-white'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex justify-between items-center">
                    <span>{getLocationDisplayName(location)}</span>
                    <span className={`text-xs ${
                      selectedLocation === location ? 'text-blue-100' : 'text-gray-500'
                    }`}>
                      {selectedCount > 0 ? `${selectedCount}/${totalCount}` : totalCount}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
        
        {/* Main Content - Golf Course Grid */}
        <div className="flex-1 overflow-y-auto border border-gray-200 rounded-md bg-white h-96 md:h-auto">
          {isLoading ? (
            <div className="flex items-center justify-center p-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
              <span className="ml-3 text-gray-600">Loading golf course information...</span>
            </div>
          ) : (
            <div className="p-4">
              {filteredCourses.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-2 gap-3">
                  {filteredCourses.map(course => (
                    <label key={course.id} className="flex items-center p-3 border border-gray-200 rounded-md hover:bg-gray-50 transition-colors cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedCourseIds.includes(course.id)}
                        onChange={(e) => onCourseChange(course.id, e.target.checked)}
                        className="mr-3 h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                      />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">{course.name}</div>
                        <div className="text-xs text-gray-500">{course.location}</div>
                      </div>
                    </label>
                  ))}
                </div>
              ) : (
                <div className="text-center text-gray-500 py-8">
                  {searchTerm || selectedLocation !== 'all' ? 'No matching golf courses found' : 'No golf course data available'}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      
      {error && (
        <p className="text-red-500 text-sm mt-2">{error}</p>
      )}
    </div>
  );
};

export default CourseSelector;